// @flow
import { retrieveText } from "../../utils/helpers/text";
import { hasAllContentInData } from "../../constants/Settings";
import SectionModel from "../content/SectionModel";

import LayoutHintCollection from "../layouthint/LayoutHintCollection";
import { isPlainObject } from "../../utils";

import type {
  ContentData,
  ContentElementMapped,
  PropertyData,
  PropertyElementMapped,
  TextFragmentData,
  TextFragmentElementMapped,
  ContentAll,
} from "../types";
import type SubSectionModel from "../content/SubSectionModel";

/**
 */
class AttributeContent {
  _content: ?ContentData;

  constructor(content: ?ContentData) {
    this._content = content;
  }

  /**
   */
  get fromData(): boolean {
    return hasAllContentInData();
  }

  /**
   */
  get header(): {
    label: ?string,
    description: ?string,
  } {
    let headerLabel = null;
    let headerDescription = null;

    const label = this._content?.header?.label;
    if (label) {
      headerLabel = label;
    }

    const description = this._content?.header?.description;
    if (description) {
      if (isPlainObject(description) && "message" in description) {
        headerDescription = description.message;
      } else if (typeof description === "string") {
        headerDescription = description;
      }
    }

    return { label: headerLabel, description: headerDescription };
  }

  /**
   */
  get elements(): ContentAll {
    if (this._content?.elements) {
      return this._content.elements
        .filter((element) => element !== null)
        .map((element) => {
          if (element.propertyElement) {
            const { label, layouthint, properties } = element.propertyElement;

            return {
              propertyElement: {
                label,
                layouthint: new LayoutHintCollection(layouthint),
                properties,
              },
            };
          }

          if (element.textFragmentElement) {
            const { label, layouthint, textfragments } =
              element.textFragmentElement;
            return {
              textFragmentElement: {
                label,
                layouthint: new LayoutHintCollection(layouthint),
                textfragments: textfragments.map((textfragment) => ({
                  ...textfragment,
                  text: retrieveText(textfragment.text) || "",
                })),
              },
            };
          }

          if (element.contentElement) {
            const { label, layouthint, sections } = element.contentElement;
            return {
              contentElement: {
                label,
                layouthint: new LayoutHintCollection(layouthint),
                sections: sections.map(
                  (section) => new SectionModel(section, null),
                ),
              },
            };
          }

          // $FlowIssue
          return element;
        });
    }

    return [];
  }

  /**
   */
  get label(): ?string {
    return this._content?.label;
  }

  /**
   */
  getContentElements<T>(elementName: string): Array<T> {
    // $FlowIssue incompatible-return
    return this.elements.filter((element) => elementName in element);
  }

  /**
   */
  getContentElementCollections<T>(
    elementName: string,
    collectionName: string,
  ): Array<T> {
    return this.getContentElements(elementName)
      .map((element) => element[elementName][collectionName])
      .flat();
  }

  /**
   */
  getContentElementCollectionByLabels<T, U>(
    elementName: string,
    collectionName: string,
    labels: Array<string>,
  ): Array<U> {
    return (
      this.getContentElements<T>(elementName)
        // $FlowIssue incompatible-use
        .filter((element) => labels.includes(element[elementName].label))
        // $FlowIssue incompatible-use
        .map((element) => element[elementName][collectionName])
        .flat()
    );
  }

  /**
   */
  get properties(): Array<PropertyData> {
    return this.getContentElementCollections<PropertyData>(
      "propertyElement",
      "properties",
    );
  }

  /**
   * Get concept properties by property element label
   */
  getConceptPropertiesByLabels(labels: Array<string>): Array<PropertyData> {
    return this.getContentElementCollectionByLabels<
      PropertyElementMapped,
      PropertyData,
    >("propertyElement", "properties", labels);
  }

  /**
   */
  get textfragments(): Array<TextFragmentData> {
    return this.getContentElementCollections<TextFragmentData>(
      "textFragmentElement",
      "textfragments",
    );
  }

  /**
   * Get concept text fragments by text fragment element label
   */
  getTextFragmentsByLabels(labels: Array<string>): Array<TextFragmentData> {
    return this.getContentElementCollectionByLabels<
      TextFragmentElementMapped,
      TextFragmentData,
    >("textFragmentElement", "textfragments", labels);
  }

  /**
   */
  get sections(): Array<SectionModel> {
    return this.getContentElementCollections<SectionModel>(
      "contentElement",
      "sections",
    );
  }

  /**
   */
  getSectionsByType(types: Array<string>): Array<SectionModel> {
    return this.sections.filter((section) => types.includes(section.type));
  }

  /**
   * Get concept text fragments by text fragment element label
   */
  getSectionsByLabels(labels: Array<string>): Array<SectionModel> {
    return this.getContentElementCollectionByLabels<
      ContentElementMapped,
      SectionModel,
    >("contentElement", "sections", labels);
  }

  /**
   */
  getReferredSections(): Array<SectionModel | SubSectionModel> {
    const referredSections = [];

    for (const section of this.sections) {
      referredSections.push(...section.getReferredSections());
    }

    return referredSections;
  }
}

export default AttributeContent;
